/**
 * Oggi sul telefono (7/10/2026): risponde a "cosa devo fare oggi?".
 *
 * Prima, a 390px, la home era la copia della Situazione: la stessa frase di sei
 * righe, gli stessi tre numeri, poi quattro riquadri sul lavoro in mano e due
 * elenchi lunghi, e le cose da fare arrivavano in fondo. Qui l'ordine è quello
 * di chi apre il telefono la mattina:
 *   1. il saluto con la data e quante cose sono urgenti;
 *   2. le cose da fare oggi (le urgenti per prime, in rosso);
 *   3. i soldi in quattro righe, ognuna porta alla pagina che la spiega;
 *   4. la settimana e gli avvisi, chiusi finché non si aprono.
 * L'analisi del lavoro in mano e gli elenchi di fatture e pagamenti restano al
 * computer e nell'Amministrazione: dal telefono si arriva lì con un tocco.
 * Stesse chiamate e stessa cache della pagina del computer.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur, euro } from '../../lib/utils';
import { canReadAdmin } from '../admin/adminApi';
import { ADMIN_PATH } from '../../lib/adminRoutes';
import { situazioneQuery } from '../admin/situazione/situazioneApi';
import { MEmpty, MGroup, MHello, MMore, MPage, MRow, leggibile } from '../../components/mobile/MobileKit';
import { useActionStream, type ActionItem } from './useActionStream';
import { oggiQuery } from './oggiApi';

const PRIMI = 6;

/**
 * "Fattura 2026/31 · Run Factory di Urso Salvatore": su 390px il nome del
 * cliente si tagliava a "Run Fa…". Nella riga del telefono il titolo è il
 * cliente, e il riferimento del documento scende nella riga sotto.
 */
export function dividi(it: ActionItem): { title: string; sub: string } {
  const i = it.title.indexOf(' · ');
  if (i > 0 && /^(Fattura|Preventivo|Proforma|Nota|Rata)\b/i.test(it.title)) {
    return { title: leggibile(it.title.slice(i + 3)), sub: [it.title.slice(0, i), it.sub].filter(Boolean).join(' · ') };
  }
  return { title: leggibile(it.title), sub: it.sub };
}

export function OggiMobile() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canAdmin = canReadAdmin(user);
  const { bands, counts, isLoading } = useActionStream();
  const { data: oggiDati } = useQuery({ ...oggiQuery(), enabled: canAdmin, retry: 1 });
  const { data: sit } = useQuery({ ...situazioneQuery({ mesi: 3 }), enabled: canAdmin, retry: 1 });
  const [tutteOggi, setTutteOggi] = useState(false);
  const [settimanaAperta, setSettimanaAperta] = useState(false);
  const [avvisiAperti, setAvvisiAperti] = useState(false);

  const oggiIso = oggiDati?.today ?? sit?.oggi ?? new Date().toISOString().slice(0, 10);
  const dataLunga = new Date(`${oggiIso}T00:00:00.000Z`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

  const banda = (n: number) => bands.find((b) => b.band === n)?.items ?? [];
  const daFareOggi = [...banda(0), ...banda(1)];
  const settimana = banda(2);
  const avvisi = banda(3);
  const visibili = tutteOggi ? daFareOggi : daFareOggi.slice(0, PRIMI);

  const riga = (it: ActionItem) => {
    const d = dividi(it);
    return (
      <MRow
        key={it.key}
        icon={it.icon}
        // Il rosso sta nell'icona e basta: con anche gli importi rossi la lista
        // era tutta un allarme, e un allarme ovunque non si legge più.
        tone={it.band === 0 ? 'alert' : undefined}
        title={d.title}
        sub={d.sub}
        trail={it.amount != null ? `${euro(it.amount)}` : undefined}
        onClick={() => navigate(it.entity.path)}
      />
    );
  };

  const stretto = sit?.previsione.mesi.find((m) => m.mese === sit.previsione.piuStretto) ?? null;
  // Dei problemi gravi qui resta solo l'aggiornamento fallito: il mese sotto
  // zero lo dice la riga del saldo, le tasse entro 7 giorni e le fatture oltre
  // 60 giorni sono già fra le cose da fare. Ripeterli erano doppioni.
  const problemi = (sit?.problemi ?? []).filter((p) => p.tipo === 'SYNC');

  return (
    <MPage>
      <MHello
        nome={user?.firstName}
        sotto={
          <>
            {dataLunga.charAt(0).toUpperCase() + dataLunga.slice(1)}
            {counts.critical > 0 && (
              <> · <span style={{ color: 'var(--alert)', fontWeight: 600 }}>{counts.critical} {counts.critical === 1 ? 'cosa urgente' : 'cose urgenti'}</span></>
            )}
          </>
        }
      />

      <MGroup title="Da fare oggi" count={daFareOggi.length}>
        {isLoading && !daFareOggi.length ? (
          <MEmpty>Carico le cose da fare…</MEmpty>
        ) : daFareOggi.length === 0 ? (
          <MEmpty>Oggi non c'è niente da fare con urgenza.{settimana.length > 0 && ' Le prossime cose sono qui sotto, nella settimana.'}</MEmpty>
        ) : (
          <>
            {visibili.map(riga)}
            {daFareOggi.length > PRIMI && (
              <MMore label={tutteOggi ? 'Mostra meno' : `Vedi tutte le ${daFareOggi.length}`} onClick={() => setTutteOggi((v) => !v)} />
            )}
          </>
        )}
      </MGroup>

      {canAdmin && sit && (
        <MGroup title="Soldi" action={{ label: 'Calendario', onClick: () => navigate(ADMIN_PATH.calendario) }}>
          <MRow
            icon="landmark"
            title="Sul conto oggi"
            sub={sit.disponibili.bancaAggiornataTesto}
            trail={`${euro(sit.disponibili.totale)}`}
            onClick={() => navigate(ADMIN_PATH.banca)}
          />
          {stretto && (
            <MRow
              icon="chart"
              tone={stretto.saldoFine < 0 ? 'alert' : undefined}
              title={`Saldo a fine ${stretto.nome.toLowerCase()}`}
              sub="previsto, il mese più basso"
              trail={`${stretto.saldoFine < 0 ? '−' : ''}${euro(Math.abs(stretto.saldoFine))}`}
              trailTone={stretto.saldoFine < 0 ? 'alert' : undefined}
              onClick={() => navigate(ADMIN_PATH.calendario)}
            />
          )}
          <MRow
            icon="invoice"
            title="Da incassare dai clienti"
            sub={sit.clienti.fattureInRitardo > 0
              ? <span className="is-alert">{sit.clienti.fattureInRitardo} {sit.clienti.fattureInRitardo === 1 ? 'fattura in ritardo' : 'fatture in ritardo'}</span>
              : 'nessuna fattura in ritardo'}
            trail={`${euro(sit.clienti.totale)}`}
            onClick={() => navigate(ADMIN_PATH.fatture)}
          />
          {sit.scaduti.totale > 0 && (
            <MRow
              icon="calendarClock"
              tone="alert"
              title="Pagamenti scaduti"
              sub={sit.scaduti.descrizione}
              subWrap
              trail={`${euro(sit.scaduti.totale)}`}
              onClick={() => navigate(ADMIN_PATH.debiti)}
            />
          )}
          {/* Se un aggiornamento non va, i numeri qui sopra sono vecchi: si dice in fondo, con il motivo. */}
          {problemi.map((p, i) => (
            <MRow key={`${p.tipo}-${i}`} icon="refresh" tone="alert" title={p.testo} sub={p.dettaglio ?? undefined} onClick={() => navigate(ADMIN_PATH.calendario)} />
          ))}
        </MGroup>
      )}

      {settimana.length > 0 && (
        <MGroup title="In settimana" count={settimana.length}>
          {(settimanaAperta ? settimana : settimana.slice(0, 3)).map(riga)}
          {settimana.length > 3 && (
            <MMore label={settimanaAperta ? 'Mostra meno' : `Vedi tutte le ${settimana.length}`} onClick={() => setSettimanaAperta((v) => !v)} />
          )}
        </MGroup>
      )}

      {avvisi.length > 0 && (
        <MGroup title="Avvisi">
          {avvisiAperti ? avvisi.map(riga) : (
            <MRow icon="bell" title={avvisi.length === 1 ? '1 da leggere' : `${avvisi.length} da leggere`} sub={avvisi[0]?.title} onClick={() => setAvvisiAperti(true)} />
          )}
        </MGroup>
      )}
    </MPage>
  );
}
